import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

// Se for usar a logo em SVG igual às outras páginas, basta importar e trocar o fallback.
import logoIconPng from './logo-icon.svg';

const planos = {
  start: {
    codigo: 'start',
    nome: 'Start',
    empresas: 1,
    valor: 189,
    descricao: 'Ideal para uma ótica começando na Lumix.'
  },
  crescimento: {
    codigo: 'crescimento',
    nome: 'Crescimento',
    empresas: 2,
    valor: 299,
    descricao: 'Para quem possui duas empresas/óticas.'
  },
  multiempresas: {
    codigo: 'multiempresas',
    nome: 'Multiempresas',
    empresas: 3,
    valor: 399,
    descricao: 'Para operações com até três empresas/óticas.'
  }
};

function pegarApiUrl() {
  return String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
}

async function lerJson(response) {
  const texto = await response.text();
  if (!texto) return {};
  try {
    return JSON.parse(texto);
  } catch {
    return { error: texto };
  }
}

function formatarDinheiro(valor) {
  return Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}

export default function ContinuarPagamento() {
  const [searchParams] = useSearchParams();

  const emailUrl = searchParams.get('email') || '';
  const tokenUrl = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [loadingPagamento, setLoadingPagamento] = useState(false);
  const [erro, setErro] = useState('');
  const [dados, setDados] = useState(null);
  const [planoSelecionado, setPlanoSelecionado] = useState('start');
  const [emailVerificationToken, setEmailVerificationToken] = useState(tokenUrl);

  useEffect(() => {
    function carregarDadosVerificados() {
      try {
        setLoading(true);
        setErro('');

        if (!emailUrl || !tokenUrl) {
          setErro('E-mail ou token de verificação não informado.');
          return;
        }

        const salvo = sessionStorage.getItem('lumix_checkout_verificado');

        if (!salvo) {
          setErro(
            'Não encontramos a confirmação deste e-mail nesta sessão. Volte ao checkout e confirme o código novamente.'
          );
          return;
        }

        const dadosSalvos = JSON.parse(salvo);

        const mesmoEmail =
          String(dadosSalvos?.email || '').toLowerCase() ===
          String(emailUrl || '').toLowerCase();

        const mesmoToken =
          String(dadosSalvos?.emailVerificationToken || '') === String(tokenUrl || '');

        if (!mesmoEmail || !mesmoToken) {
          setErro(
            'A confirmação de e-mail não corresponde a este pagamento. Confirme o e-mail novamente.'
          );
          return;
        }

        if (dadosSalvos?.tipo === 'conta_ativa') {
          setDados(dadosSalvos);
          return;
        }

        if (dadosSalvos?.tipo !== 'pagamento_pendente') {
          setErro('Nenhum pagamento pendente encontrado para este e-mail.');
          return;
        }

        setDados(dadosSalvos);
        setEmailVerificationToken(dadosSalvos.emailVerificationToken || tokenUrl);

        if (dadosSalvos?.planoCodigo && planos[dadosSalvos.planoCodigo]) {
          setPlanoSelecionado(dadosSalvos.planoCodigo);
        }
      } catch (error) {
        setErro('Não foi possível carregar os dados do pagamento.');
      } finally {
        setLoading(false);
      }
    }

    carregarDadosVerificados();
  }, [emailUrl, tokenUrl]);

  const planoAtual = useMemo(() => {
    return planos[planoSelecionado] || planos.start;
  }, [planoSelecionado]);

  const mudouPlano = useMemo(() => {
    if (!dados?.planoCodigo) return false;
    return dados.planoCodigo !== planoSelecionado;
  }, [dados, planoSelecionado]);

  function continuarPagamentoAtual() {
    if (!dados?.checkoutUrl) {
      setErro('Esse pré-cadastro ainda não possui link de pagamento válido.');
      return;
    }
    window.location.href = dados.checkoutUrl;
  }

  async function gerarPagamentoComPlanoSelecionado() {
    try {
      setErro('');
      setLoadingPagamento(true);

      if (!dados?.email) return setErro('E-mail não informado.');
      if (!dados?.sessionId) return setErro('Sessão de checkout não encontrada.');
      if (!emailVerificationToken) return setErro('Confirme seu e-mail novamente antes de alterar o plano.');

      const API_URL = pegarApiUrl();

      const response = await fetch(`${API_URL}/api/checkout/alterar-plano`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: dados.email,
          sessionId: dados.sessionId,
          planoCodigo: planoSelecionado,
          emailVerificationToken
        })
      });

      const data = await lerJson(response);

      if (!response.ok) {
        throw new Error(data?.error || data?.message || 'Não foi possível atualizar o plano.');
      }

      const checkoutUrl = data?.checkoutUrl || data?.init_point || data?.paymentUrl || data?.sandbox_init_point;

      if (!checkoutUrl) throw new Error('O backend não retornou o link de pagamento.');

      const dadosAtualizados = {
        ...dados,
        planoCodigo: data.planoCodigo || planoSelecionado,
        planoNome: data.planoNome || planoAtual.nome,
        quantidadeEmpresas: data.quantidadeEmpresas || planoAtual.empresas,
        valorMensal: data.valorMensal || planoAtual.valor,
        checkoutUrl,
        emailVerificationToken
      };

      sessionStorage.setItem('lumix_checkout_verificado', JSON.stringify(dadosAtualizados));

      window.location.href = checkoutUrl;
    } catch (error) {
      setErro(error.message || 'Erro ao gerar novo pagamento.');
    } finally {
      setLoadingPagamento(false);
    }
  }

  function handleBotaoPagamento() {
    if (mudouPlano) {
      gerarPagamentoComPlanoSelecionado();
      return;
    }
    continuarPagamentoAtual();
  }

  return (
    <div className="continue-wrapper">
      <style>{css}</style>

      <main className="continue-card animate-fade-in">
        
        <header className="card-header">
          <Link to="/" className="brand-logo">
            <img src={logoIconPng} className="logo-icon" alt="Logo" />
           
            <span className="logo-text-font">
              Lumix&nbsp;
              <span style={{ color: 'var(--blue)' }}>Ó</span>
              <span style={{ color: 'var(--red)' }}>t</span>
              <span style={{ color: 'var(--yellow)' }}>i</span>
              <span style={{ color: 'var(--green)' }}>c</span>
              <span style={{ color: 'var(--blue)' }}>a</span>
              <span style={{ color: 'var(--red)' }}>s</span>
            </span>
          </Link>
        </header>

        {/* LOADING STATE */}
        {loading && (
          <div className="state-box">
            <SpinnerIcon />
            <h2>Carregando informações...</h2>
            <p>Estamos validando a segurança da sua sessão.</p>
          </div>
        )}

        {/* ERRO DE SESSÃO */}
        {!loading && erro && !dados && (
          <div className="state-box error-state">
            <AlertIcon />
            <h2>Não foi possível continuar</h2>
            <p>{erro}</p>
            <div className="action-stack">
              <Link to="/checkout/start" className="btn-primary">Confirmar e-mail novamente</Link>
              <Link to="/" className="btn-secondary">Voltar ao início</Link>
            </div>
          </div>
        )}

        {/* CONTA JÁ ATIVA */}
        {!loading && !erro && dados?.tipo === 'conta_ativa' && (
          <div className="state-box">
            <span className="badge badge-success">Conta Ativa</span>
            <h2>Você já possui acesso.</h2>
            <p>O e-mail <strong>{dados.email}</strong> já está cadastrado em nossa plataforma. Faça login para acessar seu painel.</p>
            <div className="action-stack">
              <Link to="/login" className="btn-primary">Ir para o Login</Link>
            </div>
          </div>
        )}

        {/* FLUXO PRINCIPAL - PAGAMENTO PENDENTE */}
        {!loading && dados?.tipo === 'pagamento_pendente' && (
          <div className="checkout-flow">
            <span className="badge badge-neutral">Pagamento Pendente</span>
            
            <h1 className="main-title">Continue sua assinatura.</h1>
            <p className="main-subtitle">
              Recuperamos os dados da sua empresa. Escolha o plano desejado abaixo para finalizar a ativação em ambiente seguro.
            </p>

            {/* INFO GRID */}
            <div className="info-grid">
              <div className="info-box">
                <span className="info-label">Responsável</span>
                <span className="info-value">{dados.nomeResponsavel}</span>
              </div>
              <div className="info-box">
                <span className="info-label">E-mail corporativo</span>
                <span className="info-value">{dados.email}</span>
              </div>
              <div className="info-box col-span-2">
                <span className="info-label">Ótica / Empresa</span>
                <span className="info-value">{dados.nomeEmpresa}</span>
              </div>
            </div>

            {/* SELEÇÃO DE PLANOS */}
            <div className="plans-container">
              <div className="section-heading">
                <div>
                  <span className="section-kicker">Assinatura</span>
                  <h3 className="section-title">Confirme seu plano</h3>
                </div>
                <span className="section-hint">Você pode alterar antes de pagar</span>
              </div>
              
              <div className="plans-list">
                {Object.values(planos).map((plano) => {
                  const ativo = planoSelecionado === plano.codigo;

                  return (
                    <button
                      key={plano.codigo}
                      type="button"
                      className={`plan-option ${ativo ? 'active' : ''}`}
                      onClick={() => setPlanoSelecionado(plano.codigo)}
                      disabled={loadingPagamento}
                    >
                      <div className={`plan-check ${ativo ? 'active' : ''}`}>
                        {ativo ? <CheckMiniIcon /> : null}
                      </div>

                      <div className="plan-details">
                        <div className="plan-name-row">
                          <strong>{plano.nome}</strong>
                          <span>{plano.empresas === 1 ? '1 empresa' : `Até ${plano.empresas} empresas`}</span>
                        </div>
                        <span className="plan-desc">{plano.descricao}</span>
                      </div>

                      <div className="plan-pricing">
                        <strong>{formatarDinheiro(plano.valor)}</strong>
                        <span>/mês</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* AVISO DE MUDANÇA E ERROS */}
            {mudouPlano && !erro && (
              <div className="toast toast-info">
                Você alterou o plano. Vamos gerar um novo link de pagamento com o valor atualizado.
              </div>
            )}

            {erro && <div className="toast toast-error"><AlertIcon /> {erro}</div>}

            {/* ACTIONS */}
            <div className="action-stack mt-8">
              <button
                type="button"
                className="btn-primary"
                onClick={handleBotaoPagamento}
                disabled={loadingPagamento}
              >
                {loadingPagamento ? 'Preparando pagamento...' : (mudouPlano ? 'Atualizar plano e continuar' : 'Continuar para pagamento')}
                {!loadingPagamento && <ArrowRightIcon />}
              </button>

              <Link to="/" className="btn-secondary">Voltar ao início</Link>
            </div>
            
            <div className="secure-badge">
              <div className="secure-icon-wrap"><LockIcon /></div>
              <div>
                <strong>Pagamento seguro</strong>
                <span>Processado pelo Mercado Pago</span>
              </div>
            </div>

          </div>
        )}
      </main>
    </div>
  );
}

// ÍCONES SVG MINIMALISTAS
const SpinnerIcon = () => <svg className="animate-spin" width="32" height="32" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.2"></circle><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>;
const AlertIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>;
const CheckCircleIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>;
const ArrowRightIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>;
const LockIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>;
const CheckMiniIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>;

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Nunito:wght@700;800;900&display=swap');

  :root {
    --blue: #1a73e8;
    --blue-hover: #1765cc;
    --blue-soft: #eaf2ff;
    --red: #ea4335;
    --yellow: #fbbc04;
    --green: #34a853;

    --bg: #f7f9fc;
    --surface: #ffffff;
    --surface-soft: #f8fafc;

    --text: #171a21;
    --text-2: #343943;
    --muted: #6b7280;
    --soft: #9299a6;

    --border: #e3e8ef;
    --border-strong: #d3dae5;

    --error: #d93025;
    --error-bg: #fff2f0;
    --success: #188038;
    --success-bg: #edf8f0;
    --info-bg: #f3f8ff;

    --shadow-sm: 0 1px 2px rgba(15,23,42,.04), 0 1px 3px rgba(15,23,42,.03);
    --shadow-md: 0 14px 38px rgba(30,41,59,.08);
    --shadow-lg: 0 28px 80px rgba(30,41,59,.11);

    --font-ui: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    --font-brand: 'Nunito', 'Inter', system-ui, sans-serif;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  html {
    min-height: 100%;
    background: var(--bg);
  }

  body {
    margin: 0;
    min-height: 100%;
    background:
      radial-gradient(circle at 5% 0%, rgba(26,115,232,.06), transparent 28rem),
      radial-gradient(circle at 100% 0%, rgba(52,168,83,.035), transparent 24rem),
      var(--bg);
    color: var(--text);
    font-family: var(--font-ui);
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  button,
  input {
    font: inherit;
  }

  a {
    color: inherit;
  }

  button:focus-visible,
  a:focus-visible {
    outline: 3px solid rgba(26,115,232,.16);
    outline-offset: 3px;
  }

  .animate-fade-in {
    animation: fadeIn .28s cubic-bezier(.2,.8,.2,1) both;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .animate-spin {
    animation: spin .85s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* PAGE */
  .continue-wrapper {
    min-height: 100vh;
    padding: 34px 20px;
    display: grid;
    place-items: center;
  }

  .continue-card {
    width: min(100%, 650px);
    padding: 30px;
    background: rgba(255,255,255,.94);
    border: 1px solid var(--border);
    border-radius: 26px;
    box-shadow: var(--shadow-lg);
    backdrop-filter: blur(14px);
  }

  /* BRAND */
  .card-header {
    margin-bottom: 30px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .brand-logo {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
  }

  .logo-icon {
    width: 31px;
    height: 31px;
    object-fit: contain;
    flex-shrink: 0;
  }

  .logo-text-font {
    font-family: var(--font-brand);
    font-size: 20px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: -.035em;
    color: var(--text);
    white-space: nowrap;
  }

  /* STATES */
  .state-box {
    padding: 32px 12px 20px;
    text-align: center;
  }

  .state-box > svg {
    color: var(--blue);
  }

  .state-box h2 {
    margin: 16px 0 8px;
    font-family: var(--font-brand);
    font-size: 25px;
    line-height: 1.15;
    font-weight: 900;
    letter-spacing: -.035em;
  }

  .state-box p {
    max-width: 460px;
    margin: 0 auto 28px;
    color: var(--muted);
    font-size: 14px;
    line-height: 1.65;
    font-weight: 500;
  }

  .error-state > svg {
    width: 44px;
    height: 44px;
    color: var(--error);
  }

  /* BADGES / INTRO */
  .badge {
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 0 10px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .07em;
  }

  .badge-neutral {
    color: var(--blue);
    background: var(--blue-soft);
  }

  .badge-success {
    color: var(--success);
    background: var(--success-bg);
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    color: var(--blue);
    font-size: 10px;
    line-height: 1;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .08em;
  }

  .eyebrow-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--blue);
    box-shadow: 0 0 0 5px rgba(26,115,232,.10);
  }

  .main-title {
    margin: 0 0 10px;
    font-family: var(--font-brand);
    font-size: clamp(32px, 6vw, 42px);
    line-height: 1.04;
    font-weight: 900;
    letter-spacing: -.045em;
  }

  .main-subtitle {
    max-width: 560px;
    margin: 0;
    color: var(--muted);
    font-size: 14px;
    line-height: 1.65;
    font-weight: 500;
  }

  .trust-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 18px;
    margin-top: 17px;
  }

  .trust-row span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--muted);
    font-size: 10px;
    font-weight: 650;
  }

  .trust-row svg {
    width: 14px !important;
    height: 14px !important;
    color: var(--green);
    stroke: currentColor !important;
  }

  /* INFO GRID */
  .info-grid {
    margin: 28px 0 34px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .info-box {
    min-width: 0;
    padding: 14px;
    border: 1px solid var(--border);
    border-radius: 14px;
    background: var(--surface-soft);
  }

  .col-span-2 {
    grid-column: span 2;
  }

  .info-label {
    display: block;
    margin-bottom: 5px;
    color: var(--soft);
    font-size: 9px;
    line-height: 1;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .07em;
  }

  .info-value {
    display: block;
    overflow: hidden;
    color: var(--text-2);
    font-size: 12px;
    line-height: 1.4;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* PLANS */
  .plans-container {
    margin-bottom: 24px;
  }

  .section-heading {
    margin-bottom: 13px;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 18px;
  }

  .section-kicker {
    display: block;
    margin-bottom: 3px;
    color: var(--soft);
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .075em;
  }

  .section-title {
    margin: 0;
    color: var(--text);
    font-family: var(--font-brand);
    font-size: 18px;
    line-height: 1.15;
    font-weight: 850;
    letter-spacing: -.025em;
  }

  .section-hint {
    max-width: 170px;
    color: var(--soft);
    font-size: 9px;
    line-height: 1.35;
    font-weight: 550;
    text-align: right;
  }

  .plans-list {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .plan-option {
    width: 100%;
    min-height: 74px;
    padding: 12px 14px;
    display: grid;
    grid-template-columns: 22px minmax(0,1fr) auto;
    gap: 11px;
    align-items: center;
    text-align: left;
    color: var(--text);
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 15px;
    cursor: pointer;
    transition: border-color .16s ease, background .16s ease, box-shadow .16s ease, transform .16s ease;
  }

  .plan-option:hover:not(:disabled) {
    border-color: #c8d4e2;
    background: #fbfdff;
    box-shadow: 0 5px 16px rgba(30,41,59,.045);
    transform: translateY(-1px);
  }

  .plan-option:disabled {
    opacity: .5;
    cursor: not-allowed;
  }

  .plan-option.active {
    border-color: #9ec0f1;
    background: linear-gradient(180deg, #f8fbff 0%, #f3f8ff 100%);
    box-shadow: inset 0 0 0 1px rgba(26,115,232,.08), 0 6px 18px rgba(26,115,232,.06);
  }

  .plan-check {
    width: 21px;
    height: 21px;
    display: grid;
    place-items: center;
    border: 1px solid #d2dae5;
    border-radius: 7px;
    background: #fff;
    color: #fff;
  }

  .plan-check.active {
    border-color: var(--blue);
    background: var(--blue);
    box-shadow: 0 3px 9px rgba(26,115,232,.18);
  }

  .plan-details {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .plan-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .plan-name-row strong {
    color: var(--text);
    font-size: 12px;
    font-weight: 800;
  }

  .plan-name-row span {
    padding: 3px 6px;
    border-radius: 999px;
    background: #f1f4f7;
    color: var(--muted);
    font-size: 8px;
    font-weight: 750;
    white-space: nowrap;
  }

  .plan-option.active .plan-name-row span {
    background: var(--blue-soft);
    color: var(--blue);
  }

  .plan-desc {
    color: var(--muted);
    font-size: 10px;
    line-height: 1.45;
    font-weight: 500;
  }

  .plan-pricing {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    text-align: right;
    white-space: nowrap;
  }

  .plan-pricing strong {
    color: var(--text);
    font-family: var(--font-brand);
    font-size: 16px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: -.02em;
  }

  .plan-pricing span {
    color: var(--soft);
    font-size: 9px;
    font-weight: 550;
  }

  /* TOASTS */
  .toast {
    margin-top: 16px;
    padding: 12px 13px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    border: 1px solid transparent;
    border-radius: 12px;
    font-size: 10px;
    line-height: 1.45;
    font-weight: 650;
  }

  .toast-info {
    color: #245da8;
    background: var(--info-bg);
    border-color: #d5e5fa;
  }

  .toast-error {
    color: #b3261e;
    background: var(--error-bg);
    border-color: #ffd5d0;
  }

  /* ACTIONS */
  .action-stack {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .mt-8 {
    margin-top: 25px;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    min-height: 50px;
    padding: 0 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 13px;
    text-decoration: none;
    font-size: 12px;
    font-weight: 800;
    transition: background .16s ease, border-color .16s ease, box-shadow .16s ease, transform .16s ease, opacity .16s ease;
  }

  .btn-primary {
    border: 1px solid var(--blue);
    background: var(--blue);
    color: #fff;
    box-shadow: 0 7px 20px rgba(26,115,232,.18);
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--blue-hover);
    border-color: var(--blue-hover);
    box-shadow: 0 10px 25px rgba(26,115,232,.22);
    transform: translateY(-1px);
  }

  .btn-primary:disabled {
    color: #9299a6;
    background: #e9edf2;
    border-color: #e9edf2;
    box-shadow: none;
    cursor: not-allowed;
  }

  .btn-secondary {
    border: 1px solid var(--border);
    background: #fff;
    color: var(--text-2);
  }

  .btn-secondary:hover {
    background: #f8fafc;
    border-color: var(--border-strong);
    transform: translateY(-1px);
  }

  /* SECURE */
  .secure-badge {
    margin-top: 18px;
    padding-top: 16px;
    display: grid;
    grid-template-columns: 34px minmax(0,1fr);
    gap: 10px;
    align-items: center;
    border-top: 1px solid var(--border);
  }

  .secure-icon-wrap {
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border-radius: 10px;
    background: var(--success-bg);
    color: var(--success);
  }

  .secure-badge > div:last-child {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .secure-badge strong {
    color: var(--text-2);
    font-size: 10px;
    font-weight: 800;
  }

  .secure-badge span {
    color: var(--soft);
    font-size: 9px;
    font-weight: 500;
  }

  /* RESPONSIVE */
  @media (max-width: 640px) {
    body {
      background:
        linear-gradient(180deg, rgba(26,115,232,.045) 0, transparent 180px),
        var(--bg);
    }

    .continue-wrapper {
      padding: 12px;
      align-items: start;
    }

    .continue-card {
      margin-top: 8px;
      padding: 20px 16px;
      border-radius: 20px;
      box-shadow: var(--shadow-md);
    }

    .card-header {
      margin-bottom: 24px;
    }

    .logo-icon {
      width: 28px;
      height: 28px;
    }

    .logo-text-font {
      font-size: 18px;
    }

    .main-title {
      font-size: 30px;
    }

    .main-subtitle {
      font-size: 12px;
    }

    .trust-row {
      gap: 8px 13px;
      margin-top: 14px;
    }

    .trust-row span {
      font-size: 9px;
    }

    .info-grid {
      margin: 23px 0 28px;
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .col-span-2 {
      grid-column: span 1;
    }

    .info-box {
      padding: 12px;
    }

    .section-heading {
      align-items: flex-start;
    }

    .section-hint {
      max-width: 120px;
    }

    .plan-option {
      grid-template-columns: 21px minmax(0,1fr) auto;
      min-height: 72px;
      padding: 11px 12px;
    }

    .plan-desc {
      font-size: 9px;
    }

    .plan-pricing strong {
      font-size: 15px;
    }

    .btn-primary,
    .btn-secondary {
      min-height: 48px;
      border-radius: 12px;
    }

    .state-box {
      padding: 26px 6px 16px;
    }

    .state-box h2 {
      font-size: 22px;
    }

    .state-box p {
      font-size: 12px;
    }
  }

  @media (max-width: 430px) {
    .plan-option {
      grid-template-columns: 21px minmax(0,1fr);
    }

    .plan-pricing {
      grid-column: 2;
      align-items: flex-start;
      flex-direction: row;
      gap: 4px;
      margin-top: -1px;
    }

    .plan-name-row {
      flex-wrap: wrap;
      gap: 5px;
    }

    .section-heading {
      display: block;
    }

    .section-hint {
      display: block;
      max-width: none;
      margin-top: 5px;
      text-align: left;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: .01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: .01ms !important;
    }
  }
`;
